import { useEffect, useState } from "react";
import {
  FiRefreshCw, FiArrowRight, FiArrowDown, FiCheck, FiX,
  FiAlertCircle, FiInfo, FiZap, FiShield, FiDollarSign,
  FiCreditCard, FiTrendingUp, FiClock, FiGlobe, FiAward
} from "react-icons/fi";
import { MdAccountBalanceWallet, MdSavings, MdPayment } from "react-icons/md";
import axios from "../api/axios";

/* ═══════════════════════════════════════════
   VIBRANT NEUROMORPHISM STYLES — Consistent with Dashboard Theme
   Blue · Green · Purple · Amber · Red palette
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .wlt-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    min-height: 100%;
    // padding: 24px 20px;
  }
  .wlt-root *, .wlt-root *::before, .wlt-root *::after { box-sizing: border-box; }
  
  .wlt-root ::-webkit-scrollbar { width: 6px; height: 6px; }
  .wlt-root ::-webkit-scrollbar-track { 
    background: #d1d6df;
    border-radius: 99px;
    box-shadow: inset 0 1px 3px rgba(0,0,0,.1);
  }
  .wlt-root ::-webkit-scrollbar-thumb { 
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    border-radius: 99px;
    box-shadow: inset 0 1px 2px rgba(255,255,255,.3), inset 0 -1px 2px rgba(0,0,0,.1);
  }

  .wlt-page {
    max-width: 1280px;
    margin: 0 auto;
    display: flex; flex-direction: column; gap: 28px;
  }
  @media (max-width: 600px) { .wlt-page { gap: 16px;padding: 6px 5px; } }

  /* ══════════════════════════════
     HERO SECTION — Vibrant Gradient
  ══════════════════════════════ */
  .wlt-hero {
    background: linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #2563eb 100%);
    border-radius: 9px;
    margin-top: 20px;
    padding: 32px 36px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 20px; flex-wrap: wrap;
    position: relative;
    box-shadow: 12px 12px 24px #8f95a0, -12px -12px 24px #ffffff;
    border: 1px solid rgba(255,255,255,0.2);
  }
  .wlt-hero::before {
    content: '';
    position: absolute;
    top: -20%;
    right: -5%;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .wlt-hero::after {
    content: '';
    position: absolute;
    bottom: -20%;
    left: -5%;
    width: 250px;
    height: 250px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(139,92,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .wlt-hero-left { position: relative; z-index: 1; }
  .wlt-hero-eyebrow {
    font-size: 12px; font-weight: 800; color: rgba(255,255,255,0.8);
    text-transform: uppercase; letter-spacing: 0.15em; margin-bottom: 10px;
  }
  .wlt-hero-title {
    font-size: clamp(24px, 5vw, 34px); font-weight: 900;
    color: #ffffff; line-height: 1.2; margin-bottom: 8px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
  }
  .wlt-hero-sub { font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.85); }
  
  .wlt-refresh-btn {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 14px 28px; border-radius: 60px;
    border: none; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 800;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    color: #2563eb;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 10px #ffffff;
    transition: all 0.2s;
    white-space: nowrap;
  }
  .wlt-refresh-btn:hover {
    transform: translateY(-2px);
    box-shadow: 15px 15px 25px #8f95a0, -8px -8px 15px #ffffff;
  }
  .wlt-refresh-btn:active {
    transform: translateY(1px);
    box-shadow: 5px 5px 10px #8f95a0, -2px -2px 5px #ffffff;
  }
  .wlt-refresh-btn.spinning svg { animation: wlt-spin 0.7s linear infinite; }
  @keyframes wlt-spin { to { transform: rotate(360deg); } }

  /* ══════════════════════════════
     WALLET CARDS — Vibrant Colors
  ══════════════════════════════ */
  .wlt-cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
  }
  @media (max-width: 860px) { .wlt-cards { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 520px) { .wlt-cards { grid-template-columns: 1fr; } }

  .wlt-card {
    border-radius: 9px;
    padding: 10px 8px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    box-shadow: 14px 14px 28px #8f95a0, -8px -8px 16px #ffffff;
  }
  .wlt-card:hover {
    transform: translateY(-6px);
    box-shadow: 18px 18px 32px #8f95a0, -10px -10px 20px #ffffff;
  }
  
  /* Blue Card - LBBP */
  .wlt-card.blue {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    border: 1px solid rgba(59,130,246,0.3);
  }
  .wlt-card.blue .wlt-card-label { color: #93c5fd; }
  .wlt-card.blue .wlt-card-amount { color: #ffffff; }
  .wlt-card.blue .wlt-card-sub { color: #bfdbfe; }
  
  /* Green Card - PU&AP */
  .wlt-card.green {
    background: linear-gradient(135deg, #064e3b, #10b981);
    border: 1px solid rgba(16,185,129,0.3);
  }
  .wlt-card.green .wlt-card-label { color: #86efac; }
  .wlt-card.green .wlt-card-amount { color: #ffffff; }
  .wlt-card.green .wlt-card-sub { color: #bbf7d0; }
  
  /* Purple Card - E-Wallet */
  .wlt-card.purple {
    background: linear-gradient(135deg, #4c1d95, #8b5cf6);
    border: 1px solid rgba(139,92,246,0.3);
  }
  .wlt-card.purple .wlt-card-label { color: #d8b4fe; }
  .wlt-card.purple .wlt-card-amount { color: #ffffff; }
  .wlt-card.purple .wlt-card-sub { color: #e9d5ff; }

  .wlt-card-top { display: flex; align-items: center; justify-content: space-between; }
  .wlt-card-icon {
    width: 56px; height: 56px; border-radius: 20px;
    display: flex; align-items: center; justify-content: center;
    font-size: 26px;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(4px);
    box-shadow: inset -2px -2px 5px rgba(0,0,0,0.1), inset 2px 2px 5px rgba(255,255,255,0.1);
  }
  .wlt-card-label {
    font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em;
    margin-bottom: 8px;
  }
  .wlt-card-amount {
    font-size: 18px; font-weight: 900;
  }
  .wlt-card-sub {
    font-size: 12px; font-weight: 600; margin-top: 12px;
    opacity: 0.9;
  }

  /* ══════════════════════════════
     TABS — Neumorphism
  ══════════════════════════════ */
  .wlt-tabs-wrap {
    border-radius: 32px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: 14px 14px 28px #8f95a0, -8px -8px 16px #ffffff;
    overflow: hidden;
  }
  .wlt-tab-bar {
    display: flex;
    background: linear-gradient(145deg, #e0e5ec, #d1d6df);
    padding: 12px 20px 0 20px;
    gap: 12px;
  }
  .wlt-tab-btn {
    flex: 1; padding: 16px 16px;
    border: none; background: transparent; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 800;
    color: #64748b;
    display: flex; align-items: center; justify-content: center;
    gap: 10px;
    border-radius: 24px 24px 0 0;
    transition: all 0.25s;
    position: relative;
  }
  .wlt-tab-btn:hover { 
    color: #2563eb; 
    background: rgba(37,99,235,0.08);
    transform: translateY(-2px);
  }
  .wlt-tab-btn.active {
    color: #ffffff;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
  }
  @media (max-width: 480px) {
    .wlt-tab-btn { font-size: 11px; padding: 14px 8px; gap: 6px; }
  }

  .wlt-tab-content { padding: 32px; background: linear-gradient(145deg, #e8edf5, #dce1e8); }
  @media (max-width: 600px) { .wlt-tab-content { padding: 20px; } }

  /* Section Title */
  .wlt-section-title {
    font-size: 14px; font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 20px;
    display: flex; align-items: center; gap: 12px;
  }
  .wlt-section-title-bar {
    width: 6px; height: 24px; border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    box-shadow: 0 2px 8px rgba(245,158,11,0.4);
  }

  /* Rules Banner */
  .wlt-rules {
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 24px;
    padding: 24px 28px; margin-bottom: 24px;
    border: 1px solid rgba(37,99,235,0.2);
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.6);
  }
  .wlt-rules-header {
    display: flex; align-items: center; gap: 12px;
    font-size: 13px; font-weight: 800; color: #2563eb;
    text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 16px;
  }
  .wlt-rules-list { display: flex; flex-direction: column; gap: 12px; }
  .wlt-rule-item {
    display: flex; align-items: flex-start; gap: 12px;
    font-size: 13px; font-weight: 600; color: #334155;
  }
  .wlt-rule-check {
    width: 22px; height: 22px; border-radius: 50%;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    display: flex; align-items: center; justify-content: center;
    color: #fff;
  }

  /* Tables */
  .wlt-table-wrap {
    border-radius: 24px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 4px 4px 10px rgba(0,0,0,0.08), inset -4px -4px 10px rgba(255,255,255,0.6);
    overflow-x: auto;
  }
  .wlt-transactions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 18px;
    padding: 18px;
  }
  .wlt-transaction-card {
    background: linear-gradient(145deg, #ffffff, #f3f6fc);
    border-radius: 24px;
    box-shadow: 8px 8px 18px rgba(0,0,0,0.08), -6px -6px 14px rgba(255,255,255,0.7);
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .wlt-transaction-card.empty-card {
    justify-content: center;
    min-height: 140px;
    text-align: center;
  }
  .wlt-transaction-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }
  .wlt-transaction-card-date {
    font-size: 13px;
    font-weight: 700;
    color: #1e3a8a;
  }
  .wlt-transaction-card-time {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
  }
  .wlt-transaction-card-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .wlt-transaction-card-type {
    font-size: 14px;
    font-weight: 800;
    text-transform: capitalize;
    color: #0f172a;
  }
  .wlt-transaction-card-desc {
    font-size: 13px;
    color: #475569;
    min-height: 38px;
  }
  .wlt-transaction-card-amount {
    margin-top: auto;
    font-size: 16px;
    font-weight: 900;
  }
  .wlt-amount.credit {
    color: #16a34a;
  }
  .wlt-amount.debit {
    color: #dc2626;
  }
  .wlt-status-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: 40px;
    font-size: 11px; font-weight: 800; text-transform: capitalize;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.7), inset -1px -1px 2px rgba(0,0,0,0.05);
  }
  .wlt-status-pill.pending   { color: #f59e0b; }
  .wlt-status-pill.approved,
  .wlt-status-pill.completed { color: #10b981; }
  .wlt-status-pill.rejected { color: #ef4444; }
  .wlt-amount { font-weight: 800; }
  .wlt-amount.credit { color: #10b981; }
  .wlt-amount.debit { color: #ef4444; }

  .wlt-status-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border-radius: 40px;
    font-size: 11px; font-weight: 800; text-transform: capitalize;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.7), inset -1px -1px 2px rgba(0,0,0,0.05);
  }
  .wlt-status-pill.pending   { color: #f59e0b; }
  .wlt-status-pill.approved,
  .wlt-status-pill.completed { color: #10b981; }
  .wlt-status-pill.rejected { color: #ef4444; }

  .wlt-empty-row td {
    text-align: center; padding: 60px 20px;
    font-size: 14px; font-weight: 600; color: #94a3b8;
  }

  /* Forms */
  .wlt-form { display: flex; flex-direction: column; gap: 24px; }
  .wlt-field-label {
    font-size: 13px; font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px;
    display: block;
  }
  .wlt-input, .wlt-select {
    width: 100%; padding: 16px 20px;
    border: none; border-radius: 32px;
    font-family: 'Montserrat', sans-serif; font-size: 15px; font-weight: 600;
    color: #1e293b; background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 5px 5px 12px rgba(0,0,0,0.08), inset -5px -5px 12px rgba(255,255,255,0.7);
    transition: all 0.2s;
    outline: none;
  }
  .wlt-input:focus, .wlt-select:focus {
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.1), inset -3px -3px 8px rgba(255,255,255,0.8);
    border: 1px solid #2563eb;
  }
  .wlt-input::placeholder { color: #94a3b8; font-weight: 500; }
  .wlt-select {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 20px center;
  }
  .wlt-amount-wrap { display: flex; align-items: center; gap: 12px; }
  .wlt-amount-prefix {
    font-size: 28px; font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    padding: 0 8px;
  }
  .wlt-hint { font-size: 11px; font-weight: 600; color: #94a3b8; margin-top: 8px; }

  /* Earned Income Display */
  .wlt-earned-income-display {
    background: linear-gradient(145deg, #f0fdf4, #dcfce7);
    border-radius: 24px;
    padding: 18px 22px; margin-top: 12px;
    border: 1px solid rgba(16,185,129,0.3);
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.04), inset -2px -2px 5px rgba(255,255,255,0.5);
  }
  .wlt-earned-income-label {
    font-size: 12px; font-weight: 800; color: #059669;
    text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 12px;
  }
  .wlt-earned-income-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-radius: 20px; margin-bottom: 10px;
    background: rgba(255,255,255,0.6);
    box-shadow: inset 1px 1px 2px rgba(255,255,255,0.8), inset -1px -1px 2px rgba(0,0,0,0.05);
  }
  .wlt-earned-income-row.available {
    background: linear-gradient(135deg, #ecfdf5, #d1fae5);
    box-shadow: 4px 4px 12px rgba(0,0,0,0.08), -2px -2px 8px rgba(255,255,255,0.5);
  }
  .wlt-earned-income-row-label {
    font-size: 13px; font-weight: 700; color: #064e3b;
  }
  .wlt-earned-income-row-amount {
    font-size: 15px; font-weight: 800;
  }
  .wlt-earned-income-row-amount.total { color: #1e293b; }
  .wlt-earned-income-row-amount.transferred { color: #ef4444; }
  .wlt-earned-income-row-amount.available { color: #10b981; }
  .wlt-earned-income-breakdown {
    display: flex; gap: 20px; margin-top: 12px;
    font-size: 12px; font-weight: 700; color: #059669;
  }

  /* Alerts */
  .wlt-alert {
    display: flex; align-items: center; gap: 14px;
    padding: 16px 22px; border-radius: 24px;
    font-size: 13px; font-weight: 700;
    margin-bottom: 8px;
  }
  .wlt-alert.error {
    background: linear-gradient(145deg, #fef2f2, #fee2e2);
    border: 1px solid rgba(239,68,68,0.3);
    color: #dc2626;
  }
  .wlt-alert.success {
    background: linear-gradient(145deg, #ecfdf5, #d1fae5);
    border: 1px solid rgba(16,185,129,0.3);
    color: #059669;
  }
  .wlt-alert.info {
    background: linear-gradient(145deg, #eff6ff, #dbeafe);
    border: 1px solid rgba(37,99,235,0.3);
    color: #2563eb;
  }

  /* Info Banner */
  .wlt-info-banner {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    border-radius: 28px;
    padding: 22px 28px;
    display: flex; align-items: center; gap: 20px;
    box-shadow: 12px 12px 24px #8f95a0, -8px -8px 16px #ffffff;
    border: 1px solid rgba(255,255,255,0.2);
  }
  .wlt-info-icon {
    width: 60px; height: 60px; border-radius: 24px; flex-shrink: 0;
    background: rgba(255,255,255,0.15);
    backdrop-filter: blur(8px);
    box-shadow: inset -2px -2px 5px rgba(0,0,0,0.1), inset 2px 2px 5px rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 24px;
  }
  .wlt-info-label { font-size: 12px; font-weight: 800; color: rgba(255,255,255,0.8); text-transform: uppercase; letter-spacing: 0.1em; }
  .wlt-info-amount { font-size: 32px; font-weight: 900; color: #fbbf24; text-shadow: 0 2px 4px rgba(0,0,0,0.1); }

  /* Submit Buttons */
  .wlt-submit-btn {
    width: 100%; padding: 18px;
    border: none; border-radius: 60px; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 15px; font-weight: 800;
    display: flex; align-items: center; justify-content: center; gap: 12px;
    transition: all 0.25s;
    letter-spacing: 0.05em;
    position: relative;
    overflow: hidden;
  }
  .wlt-submit-btn::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255,255,255,0.3);
    transform: translate(-50%, -50%);
    transition: width 0.4s, height 0.4s;
  }
  .wlt-submit-btn:hover::before {
    width: 300px;
    height: 300px;
  }
  .wlt-submit-btn.transfer {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    color: #ffffff;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 10px #ffffff;
  }
  .wlt-submit-btn.transfer:hover { transform: translateY(-3px); box-shadow: 14px 14px 24px #8f95a0, -7px -7px 14px #ffffff; }
  .wlt-submit-btn.withdraw {
    background: linear-gradient(135deg, #064e3b, #10b981);
    color: #ffffff;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 10px #ffffff;
  }
  .wlt-submit-btn.withdraw:hover { transform: translateY(-3px); box-shadow: 14px 14px 24px #8f95a0, -7px -7px 14px #ffffff; }
  .wlt-submit-btn:active { transform: translateY(1px); }
  .wlt-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .wlt-divider {
    height: 2px; background: linear-gradient(90deg, transparent, #2563eb, #8b5cf6, transparent);
    margin: 16px 0; border-radius: 99px;
  }
  .wlt-footnote {
    font-size: 12px; font-weight: 600; color: #94a3b8;
    display: flex; align-items: flex-start; gap: 10px; margin-top: 16px;
  }

  /* Skeleton Loader */
  .wlt-skeleton {
    display: flex; flex-direction: column; gap: 24px; padding: 20px;
    max-width: 1280px; margin: 0 auto;
  }
  .wlt-skel {
    border-radius: 32px;
    background: #dce1e8;
    position: relative;
    overflow: hidden;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .wlt-skel::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: wlt-shimmer 1.4s infinite;
  }
  @keyframes wlt-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }
  .wlt-skel-hero { height: 160px; }
  .wlt-skel-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 860px) { .wlt-skel-row { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 520px) { .wlt-skel-row { grid-template-columns: repeat(2, 1fr); } }
  .wlt-skel-box { height: 160px; border-radius: 28px; background: #dce1e8; position: relative; overflow: hidden; }
  .wlt-skel-box::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size: 200% 100%;
    animation: wlt-shimmer 1.4s infinite;
  }
  .wlt-skel-card { height: 380px; }

  .wlt-error {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 80px 40px; text-align: center;
    gap: 20px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    border-radius: 40px;
    margin: 40px;
    box-shadow: 20px 20px 40px #8f95a0, -10px -10px 20px #ffffff;
  }
  .wlt-error-icon { font-size: 64px; }
  .wlt-error-text { font-size: 18px; font-weight: 700; color: #ffffff; }
  .withdrawal-cards {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 14px;
}

.withdrawal-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.withdrawal-card:hover {
  box-shadow: 0 5px 16px rgba(0, 0, 0, 0.07);
  transform: translateY(-1px);
}

.withdrawal-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eeeeee;
}

.withdrawal-card-label {
  font-size: 12px;
  color: #777;
  margin-bottom: 4px;
}

.withdrawal-card-amount {
  font-size: 20px;
  font-weight: 700;
  color: #d9534f;
}

.withdrawal-card-details {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  padding-top: 15px;
}

.withdrawal-detail {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.withdrawal-detail-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.withdrawal-detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.withdrawal-empty-card {
  padding: 30px 20px;
  text-align: center;
  background: #fff;
  border: 1px dashed #d8d8d8;
  border-radius: 14px;
  color: #777;
  font-size: 14px;
}

/* Mobile */
@media (max-width: 600px) {
  .withdrawal-card {
    padding: 15px;
  }

  .withdrawal-card-header {
    align-items: flex-start;
  }

  .withdrawal-card-amount {
    font-size: 18px;
  }

  .withdrawal-card-details {
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .withdrawal-detail:last-child {
    grid-column: 1 / -1;
  }
}
`;

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const Wallet = () => {
  const [wallet, setWallet] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [kycStatus, setKycStatus] = useState(null);

  const [transferType, setTransferType] = useState("lbbp");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferError, setTransferError] = useState("");
  const [transferSuccess, setTransferSuccess] = useState("");
  const [transferEligibility, setTransferEligibility] = useState(null);

  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawMethod, setWithdrawMethod] = useState("bank");
  const [withdrawError, setWithdrawError] = useState("");
  const [withdrawSuccess, setWithdrawSuccess] = useState("");

  // Auto-fill bank details from dashboard/member API (hidden from UI)
  useEffect(() => {
    const fetchBankDetails = async () => {
      if (withdrawMethod !== "bank") return;
      try {
        const res = await axios.get("/dashboard/member");
        const bd = res.data.bankDetails || {};
        // Store bank details internally but don't show in UI
        window.tempBankDetails = {
          bankName: bd.bankName || "",
          accountNumber: bd.bankAccount || "",
          ifsc: bd.ifsc || ""
        };
      } catch (error) {
        console.log("Could not fetch bank details");
      }
    };
    fetchBankDetails();
  }, [withdrawMethod]);

  const fetchUserData = async () => {
    try {
      const res = await axios.get("/dashboard/member");
      setKycStatus(res.data.user.kycStatus);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const fetchWalletData = async (fromBtn = false) => {
    try {
      if (fromBtn) setSpinning(true);
      setLoading(true);
      const [walletRes, payoutRes, transactionRes, withdrawalRes] = await Promise.all([
        axios.get("/share/dashboard-summary"),
        axios.get("/share/my-payouts"),
        axios.get("/share/transactions"),
        axios.get("/share/withdrawal-history")
      ]);
      const walletSummary = walletRes.data || {};
      setWallet({
        ...walletSummary,
        puapWallet: walletSummary.puapWallet || 0,
        eWalletBalance: walletSummary.eWalletBalance || walletSummary.eWallet || 0,
        walletBalance: walletSummary.lbbpWallet || 0,
      });
      setPayoutHistory(payoutRes.data || []);
      setTransactions(transactionRes.data.filter((t) => t.type === "transfer_to_ewallet") || []);
      setWithdrawals(withdrawalRes.data || []);
    } catch (error) {
      console.error("Error fetching wallet:", error);
    } finally {
      setLoading(false);
      if (fromBtn) setTimeout(() => setSpinning(false), 700);
    }
  };

  useEffect(() => {
    fetchWalletData();
    fetchUserData();
  }, []);

  useEffect(() => {
    fetchTransferEligibility();
  }, [transferType]);

  const fetchTransferEligibility = async () => {
    try {
      const { data } = await axios.get(`/product/wallet/transfer-eligibility?walletType=${transferType}`);
      setTransferEligibility(data);
    } catch (error) {
      console.error("Error fetching transfer eligibility:", error);
    }
  };

  const validateAmount = (amount) => {
    if (!amount || amount < 100) return "Minimum ₹100 required";
    if (amount % 100 !== 0) return "Must be a round figure (₹100, ₹200, ₹300…)";
    return "";
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferError(""); setTransferSuccess("");
    const err = validateAmount(Number(transferAmount));
    if (err) { setTransferError(err); return; }
    try {
      await axios.post("/share/transfer-to-ewallet", { amount: Number(transferAmount), walletType: transferType });
      setTransferSuccess(`₹${transferAmount} transferred to E-Wallet successfully!`);
      setTransferAmount("");
      setTimeout(() => fetchWalletData(), 1000);
      setTimeout(() => fetchTransferEligibility(), 1000);
      setTimeout(() => setTransferSuccess(""), 4000);
    } catch (error) {
      setTransferError(error.response?.data?.message || "Transfer failed. Please try again.");
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setWithdrawError(""); setWithdrawSuccess("");
    const err = validateAmount(Number(withdrawAmount));
    if (err) { setWithdrawError(err); return; }
    try {
      // Get bank details from stored temp data (auto-filled from KYC)
      const bankDetailsToSend = withdrawMethod === "bank" 
        ? (window.tempBankDetails || { bankName: "", accountNumber: "", ifsc: "" })
        : { upiId: "" };
      
      await axios.post("/share/withdraw", { 
        amount: Number(withdrawAmount), 
        paymentMethod: withdrawMethod, 
        bankDetails: bankDetailsToSend,
        walletType: "ewallet"
      });
      setWithdrawSuccess(`Withdrawal request of ₹${withdrawAmount} submitted! Pending admin approval.`);
      setWithdrawAmount("");
      setTimeout(() => fetchWalletData(), 1000);
      setTimeout(() => setWithdrawSuccess(""), 5000);
    } catch (error) {
      setWithdrawError(error.response?.data?.message || "Withdrawal failed. Please try again.");
    }
  };

  if (loading && !wallet) return (
    <div className="wlt-root">
      <style>{styles}</style>
      <div className="wlt-skeleton">
        <div className="wlt-skel wlt-skel-hero" />
        <div className="wlt-skel-row">
          {[1, 2, 3].map(i => <div className="wlt-skel-box" key={i} />)}
        </div>
        <div className="wlt-skel wlt-skel-card" />
      </div>
    </div>
  );

  if (!wallet) return (
    <div className="wlt-root">
      <style>{styles}</style>
      <div className="wlt-error">
        <div className="wlt-error-icon">😕</div>
        <div className="wlt-error-text">Failed to load wallet data</div>
      </div>
    </div>
  );

  return (
    <div className="wlt-root">
      <style>{styles}</style>
      <div className="wlt-page">

        {/* ── Hero ── */}
        <div className="wlt-hero">
          <div className="wlt-hero-left">
            <div className="wlt-hero-eyebrow">
              <FiAward size={12} style={{ display: 'inline', marginRight: 6 }} /> Premium Finance Center
            </div>
            <div className="wlt-hero-title">Wallet Management</div>
            <div className="wlt-hero-sub">Transfer, withdraw and track your earnings securely</div>
          </div>
          <button
            className={`wlt-refresh-btn ${spinning ? "spinning" : ""}`}
            onClick={() => fetchWalletData(true)}
          >
            <FiRefreshCw size={16} /> Refresh Balance
          </button>
        </div>

        {/* ── Wallet Cards ── */}
        <div className="wlt-cards">
          <WalletCard variant="blue" emoji="💼"
            label="LBBP Wallet"
            amount={wallet.totalEarnedIncome || 0}
            sub="Loan-Based Business Partnership"
          />
          <WalletCard variant="green" emoji="🛍️"
            label="PU&AP Wallet"
            amount={wallet?.puapWallet || 0}
            sub="Product User and Advisor Partnership"
          />
          <WalletCard variant="purple" emoji="💰"
            label="E-Wallet"
            amount={wallet.eWalletBalance || 0}
            sub="Ready to Withdraw"
          />
        </div>

        {/* ── Tabs ── */}
        <div className="wlt-tabs-wrap">
          <div className="wlt-tab-bar">
            {[
              { id: "overview", icon: <FiShield size={16} />, label: "Overview" },
              { id: "transfer", icon: <FiArrowRight size={16} />, label: "Transfer" },
              { id: "withdraw", icon: <FiArrowDown size={16} />, label: "Withdraw" },
              // { id: "transactions", icon: <FiTrendingUp size={16} />, label: "Transactions" },
            ].map(t => (
              <button
                key={t.id}
                className={`wlt-tab-btn ${activeTab === t.id ? "active" : ""}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          <div className="wlt-tab-content">

            {/* ── Overview Tab ── */}
            {activeTab === "overview" && (
              <div>
                <div className="wlt-section-title">
                  <div className="wlt-section-title-bar" /> Transfer Rules
                </div>
                <div className="wlt-rules">
                  <div className="wlt-rules-header">
                    <FiZap size={16} /> How it works
                  </div>
                  <div className="wlt-rules-list">
                    {[
                      "Transfer minimum ₹100 from earned income (LBBP) to E-Wallet",
                      "Only earned income (level income, EMI, royalty) can be transferred",
                      "Amounts must be in round figures — ₹100, ₹200, ₹300…",
                      "Only withdraw from E-Wallet after transferring funds",
                      "Admin approval required for all withdrawal requests",
                    ].map((rule, i) => (
                      <div className="wlt-rule-item" key={i}>
                        <div className="wlt-rule-check"><FiCheck size={11} /></div>
                        {rule}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Transfer Tab ── */}
            {activeTab === "transfer" && (
              <div>
                <div className="wlt-section-title">
                  <div className="wlt-section-title-bar" /> Transfer to E-Wallet
                </div>

                {transferError && <Alert type="error" icon={<FiX size={16} />} msg={transferError} />}
                {transferSuccess && <Alert type="success" icon={<FiCheck size={16} />} msg={transferSuccess} />}

                <form className="wlt-form" onSubmit={handleTransfer} style={{ marginTop: 16 }}>
                  <div>
                    <label className="wlt-field-label">Source Wallet</label>
                    <select
                      className="wlt-select"
                      value={transferType}
                      onChange={(e) => setTransferType(e.target.value)}
                    >
                      <option value="lbbp">💼 LBBP Wallet — Earned Income Transfers Only</option>
                      <option value="puap">🛍️ PU&AP Wallet — Product Income Transfers</option>
                    </select>
                    <div className="wlt-hint">Transfer eligible earnings from the selected wallet to your E-Wallet for withdrawal</div>
                    {(wallet || transferEligibility) && (
                      <div className="wlt-earned-income-display">
                        <div className="wlt-earned-income-label">
                          <FiDollarSign size={12} style={{ display: 'inline', marginRight: 6 }} />
                          {transferType === "puap" ? "PU&AP Income Summary:" : "Earned Income Summary:"}
                        </div>
                        
                        <div className="wlt-earned-income-row">
                          <div className="wlt-earned-income-row-label">{transferType === "puap" ? "Total Eligible PU&AP Income:" : "Total Earned Income:"}</div>
                          <div className="wlt-earned-income-row-amount total">
                            ₹{((transferEligibility?.totalEligibleIncome ?? wallet?.totalEarnedIncome) || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                        
                        <div className="wlt-earned-income-row">
                          <div className="wlt-earned-income-row-label">Already Transferred to E-Wallet:</div>
                          <div className="wlt-earned-income-row-amount transferred">
                            -₹{((transferEligibility?.transferredAmount ?? wallet?.totalTransferred) || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                        
                        <div className="wlt-earned-income-row available">
                          <div className="wlt-earned-income-row-label">Available for Transfer:</div>
                          <div className="wlt-earned-income-row-amount available">
                            ₹{((transferEligibility?.availableToTransfer ?? wallet?.availableEarnedIncome) || 0).toLocaleString("en-IN")}
                          </div>
                        </div>
                        
                        <div className="wlt-earned-income-breakdown">
                          <span>EMI: ₹{(wallet?.totalROI || 0).toLocaleString("en-IN")}</span>
                          <span>Level: ₹{(wallet?.totalLevelIncome || 0).toLocaleString("en-IN")}</span>
                          <span>Royalty: ₹{(wallet?.totalRoyalty || 0).toLocaleString("en-IN")}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="wlt-field-label">Transfer Amount</label>
                    <div className="wlt-amount-wrap">
                      <span className="wlt-amount-prefix">₹</span>
                      <input
                        type="number"
                        className="wlt-input"
                        value={transferAmount}
                        onChange={(e) => setTransferAmount(e.target.value)}
                        placeholder="Enter amount (min. ₹100)"
                      />
                    </div>
                    <div className="wlt-hint">Round figures only: ₹100, ₹200, ₹300…</div>
                  </div>

                  <button type="submit" className="wlt-submit-btn transfer" disabled={!transferAmount}>
                    <FiArrowRight size={18} /> Transfer to E-Wallet
                  </button>
                </form>

                {/* Last 10 Transactions */}
                <div className="wlt-section-title" style={{ marginTop: 32 }}>
                  <div className="wlt-section-title-bar" /> Last 10 Transactions
                </div>
                <div className="wlt-transactions-grid">
                  {transactions.slice(0, 10).length > 0 ? transactions.slice(0, 10).map((t) => (
                    <div key={t._id} className="wlt-transaction-card">
                      <div className="wlt-transaction-card-header">
                        <div>
                          <div className="wlt-transaction-card-date">{new Date(t.createdAt).toLocaleDateString("en-IN")}</div>
                          <div className="wlt-transaction-card-time">{new Date(t.createdAt).toLocaleTimeString("en-IN")}</div>
                        </div>
                        <span className={`wlt-status-pill ${t.approvalStatus || "completed"}`}>
                          {t.approvalStatus || "completed"}
                        </span>
                      </div>
                      <div className="wlt-transaction-card-body">
                        <div className="wlt-transaction-card-type">{t.type.replace(/_/g, " ")}</div>
                        <div className="wlt-transaction-card-desc">{t.description || "No description"}</div>
                      </div>
                      <div className="wlt-transaction-card-amount" style={{ marginTop: 16 }}>
                        <span className={`wlt-amount ${t.type.includes("income") || t.type.includes("transfer") ? "credit" : "debit"}`}>
                          {t.type.includes("income") || t.type.includes("transfer") ? "+" : "-"}
                          ₹{Number(t.amount || 0).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  )) : (
                    <div className="wlt-transaction-card empty-card">
                      <div className="wlt-transaction-card-body">
                        <div className="wlt-transaction-card-desc">📭 No transactions yet</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Withdraw Tab ── */}
            {activeTab === "withdraw" && (
              <div>
                <div className="wlt-section-title">
                  <div className="wlt-section-title-bar" /> Request Withdrawal
                </div>

                {/* KYC Status Check */}
                {kycStatus !== "verified" ? (
                  <div className="wlt-alert info" style={{ marginBottom: 20 }}>
                    <FiAlertCircle size={18} />
                    <div>
                      <strong>KYC Verification Required</strong><br />
                      You must complete KYC verification before you can request withdrawals. Please complete your KYC verification first.
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="wlt-info-banner" style={{ marginBottom: 20 }}>
                      <div className="wlt-info-icon">
                        <MdPayment size={28} />
                      </div>
                      <div>
                        <div className="wlt-info-label">Available Balance</div>
                        <div className="wlt-info-amount">₹{(wallet.eWalletBalance || 0).toLocaleString()}</div>
                      </div>
                    </div>

                    {withdrawError && <Alert type="error" icon={<FiX size={16} />} msg={withdrawError} />}
                    {withdrawSuccess && <Alert type="success" icon={<FiCheck size={16} />} msg={withdrawSuccess} />}

                    <form className="wlt-form" onSubmit={handleWithdraw} style={{ marginTop: 16 }}>
                      <div>
                        <label className="wlt-field-label">Withdrawal Amount</label>
                        <div className="wlt-amount-wrap">
                          <span className="wlt-amount-prefix">₹</span>
                          <input
                            type="number"
                            className="wlt-input"
                            value={withdrawAmount}
                            onChange={(e) => setWithdrawAmount(e.target.value)}
                            placeholder="Enter amount (min. ₹100)"
                          />
                        </div>
                        <div className="wlt-hint">Round figures only: ₹100, ₹200, ₹300…</div>
                      </div>

                      <div>
                        <label className="wlt-field-label">Payment Method</label>
                        <select
                          className="wlt-select"
                          value={withdrawMethod}
                          onChange={(e) => setWithdrawMethod(e.target.value)}
                        >
                          <option value="bank">🏦 Bank Transfer</option>
                          <option value="upi">📱 UPI Transfer</option>
                        </select>
                      </div>

                      <button type="submit" className="wlt-submit-btn withdraw" disabled={!withdrawAmount}>
                        <FiArrowDown size={18} /> Submit Withdrawal Request
                      </button>
                    </form>

                    <div className="wlt-footnote" style={{ marginTop: 14 }}>
                      <FiClock size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                      Withdrawals are processed after admin approval within 2–3 business days.
                    </div>
                  </>
                )}

                {/* Last 10 Withdrawals */}
               {kycStatus === "verified" && (
  <>
    <div className="wlt-section-title" style={{ marginTop: 32 }}>
      <div className="wlt-section-title-bar" />
      Last 10 Withdrawal Requests
    </div>

    <div className="withdrawal-cards">
      {withdrawals.slice(0, 10).length > 0 ? (
        withdrawals.slice(0, 10).map((w) => (
          <div className="withdrawal-card" key={w._id}>
            {/* Card Header */}
            <div className="withdrawal-card-header">
              <div>
                <div className="withdrawal-card-label">Withdrawal Amount</div>
                <div className="withdrawal-card-amount">
                  -₹{Number(w.amount || 0).toLocaleString("en-IN")}
                </div>
              </div>

              <span className={`wlt-status-pill ${w.status}`}>
                {w.status}
              </span>
            </div>

            {/* Card Details */}
            <div className="withdrawal-card-details">
              <div className="withdrawal-detail">
                <span className="withdrawal-detail-label">Date</span>
                <span className="withdrawal-detail-value">
                  {new Date(w.createdAt).toLocaleDateString("en-IN")}
                </span>
              </div>

              <div className="withdrawal-detail">
                <span className="withdrawal-detail-label">Method</span>
                <span
                  className="withdrawal-detail-value"
                  style={{ textTransform: "capitalize" }}
                >
                  {w.paymentMethod}
                </span>
              </div>

              <div className="withdrawal-detail">
                <span className="withdrawal-detail-label">Last Updated</span>
                <span className="withdrawal-detail-value">
                  {w.updatedAt
                    ? new Date(w.updatedAt).toLocaleDateString("en-IN")
                    : "—"}
                </span>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="withdrawal-empty-card">
          📭 No withdrawal requests yet
        </div>
      )}
    </div>
  </>
)}
              </div>
            )}

            {/* ── All Transactions Tab ── */}
            {activeTab === "transactions" && (
              <div>
                <div className="wlt-section-title">
                  <div className="wlt-section-title-bar" /> All Transactions
                </div>
                <div className="wlt-transactions-grid">
                  {transactions.length > 0 ? transactions.map((t) => (
                    <div key={t._id} className="wlt-transaction-card">
                      <div className="wlt-transaction-card-header">
                        <div>
                          <div className="wlt-transaction-card-date">{new Date(t.createdAt).toLocaleDateString("en-IN")}</div>
                          <div className="wlt-transaction-card-time">{new Date(t.createdAt).toLocaleTimeString("en-IN")}</div>
                        </div>
                        <span className={`wlt-status-pill ${t.approvalStatus || "completed"}`}>
                          {t.approvalStatus || "completed"}
                        </span>
                      </div>
                      <div className="wlt-transaction-card-body">
                        <div className="wlt-transaction-card-type">{t.type.replace(/_/g, " ")}</div>
                        <div className="wlt-transaction-card-desc">{t.description || "No description"}</div>
                      </div>
                      <div className="wlt-transaction-card-amount">
                        <span className={`wlt-amount ${t.type.includes("income") || t.type.includes("transfer") ? "credit" : "debit"}`}>
                          {t.type.includes("income") || t.type.includes("transfer") ? "+" : "-"}
                          ₹{Number(t.amount || 0).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  )) : (
                    <div className="wlt-transaction-card empty-card">
                      <div className="wlt-transaction-card-body">
                        <div className="wlt-transaction-card-desc">📭 No transactions yet</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

      </div>

    </div>
  );
};

/* ═══════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════ */
const WalletCard = ({ variant, emoji, label, amount, sub }) => (
  <div className={`wlt-card ${variant}`}>
    <div className="wlt-card-top">
      <div className="wlt-card-label">{label}</div>
      <div className="wlt-card-icon">{emoji}</div>
    </div>
    <div className="wlt-card-amount">
      ₹{amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
    </div>
    <div className="wlt-card-sub">{sub}</div>
  </div>
);

const Alert = ({ type, icon, msg }) => (
  <div className={`wlt-alert ${type}`} style={{ marginBottom: 4 }}>
    {icon} {msg}
  </div>
);

export default Wallet;